import {
  DoubleRightOutlined,
  LinkOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import {
  GET_USER_BASICS_BY_ID,
  GET_USER_RANK,
  Me,
  User,
} from '@frontend/data-access';
import { Avatar, Button, Drawer, Grid, Modal, Skeleton, Spin } from 'antd';
import getConfig from 'next/config';
import Image from 'next/image';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { useEffect, useMemo, useState } from 'react';

import ProtectedCard from '../protected-card/protected-card';
import UserInfo from '../user-info/user-info';

const { useBreakpoint } = Grid;

const covertName = (name: string) => {
  const splittedStr = name.split(/(\s+)/);
  let str = '';
  splittedStr.forEach((s) => {
    if (s.trim() !== '') {
      str = str.concat(s.trim().charAt(0).toUpperCase());
    }
  });
  return str;
};

export interface UserAvatarProps {
  user: User | Me;
  size?: number | 'default' | 'large' | 'small';
  shape?: 'square' | 'circle';
  showBadge?: boolean;
  badgeSize?: number;
  className?: string;
  isClickable?: boolean;
}

export function UserAvatar({
  user,
  size = 'default',
  shape = 'circle',
  showBadge = false,
  badgeSize = 20,
  className,
  isClickable = true,
}: UserAvatarProps) {
  const { t: tcommon } = useTranslation('common');
  const screens = useBreakpoint();
  const [drawer, setDrawer] = useState<boolean>(false);
  const [helper, setHelper] = useState(false);
  const { publicRuntimeConfig } = getConfig();
  const [getRank, { data: rankData, loading: rankLoading }] = useLazyQuery<{
    findUser: User;
  }>(GET_USER_RANK);

  const [getUserInfo, { data: userData, loading }] = useLazyQuery<{
    findUser: User;
  }>(GET_USER_BASICS_BY_ID);

  const colors = [
    '!bg-[#68BAEA]',
    '!bg-[#68EA7D]',
    '!bg-[#1F3644]',
    '!bg-[#686DEA]',
    '!bg-[#EAC668]',
  ];

  const levels = useMemo(
    () => [
      { id: 1, description: tcommon('user_avatar_level_1') },
      { id: 2, description: tcommon('user_avatar_level_2') },
      { id: 3, description: tcommon('user_avatar_level_3') },
      { id: 4, description: tcommon('user_avatar_level_4') },
      { id: 5, description: tcommon('user_avatar_level_5') },
      { id: 6, description: tcommon('user_avatar_level_6') },
      { id: 7, description: tcommon('user_avatar_level_7') },
    ],
    [tcommon]
  );

  const name = user?.fullName || user?.firstName + ' ' + user?.lastName;
  const src = user?.image?.url;

  const getColor = (name: string) => {
    const charCode = name.toLowerCase().charCodeAt(0);
    const index = (charCode - 97) % colors.length;
    return colors[index];
  };

  const color = name ? getColor(name) : '!bg-primary-1000';

  const getData = (id: string) => {
    getUserInfo({
      variables: {
        id: id,
      },
    });
  };

  const handleClick = () => {
    if (isClickable) {
      handleDrawer();
      getData(user?.id.toString());
    }
  };

  const handleDrawer = () => {
    setDrawer(!drawer);
  };

  const handleHelper = () => {
    setHelper(!helper);
  };

  useEffect(() => {
    if (showBadge) {
      if (!user?.rank) {
        getRank({
          variables: {
            id: user?.id,
          },
        });
      }
    }
  }, [getRank, showBadge, user?.rank, user?.id]);

  const badgeLevel = useMemo(
    () => user?.rank?.badge?.level || rankData?.findUser?.rank?.badge.level,
    [rankData?.findUser?.rank?.badge.level, user?.rank?.badge?.level]
  );

  if (name === '' && src === null) {
    return <Avatar size={size} shape={shape} icon={<UserOutlined />} />;
  }

  return (
    <div className="relative w-fit">
      <Avatar
        size={size}
        shape={shape}
        src={src}
        className={`${className} ${color} ${isClickable && 'cursor-pointer'}`}
        onClick={handleClick}
      >
        <span data-test="profile-name">{name && covertName(name)}</span>
      </Avatar>
      {showBadge && !rankLoading && badgeLevel && (
        <div
          className={`absolute bottom-[-5px] right-[-5px] cursor-pointer select-none`}
          onClick={isClickable ? handleHelper : undefined}
        >
          <Image
            src={`${publicRuntimeConfig.staticFolder}/svg/user-badge/${badgeLevel}.svg`}
            height={badgeSize}
            width={badgeSize}
            alt="badge"
            className="drop-shadow-md aspect-square"
          />
        </div>
      )}
      <Modal
        title={tcommon('user_avatar_level_rule')}
        open={helper}
        onCancel={handleHelper}
        footer={null}
      >
        <div className="mb-2">{tcommon('intrapreneurs_ranked')}:</div>
        <ul className="list-disc space-y-2">
          {levels.map((level, index) => (
            <li key={index} className="flex items-center space-x-2">
              <Image
                src={`${publicRuntimeConfig.staticFolder}/svg/user-badge/${level.id}.svg`}
                height={20}
                width={20}
                alt="user-badge"
              />
              <span>{level.description}</span>
            </li>
          ))}
        </ul>
      </Modal>
      <Drawer
        title={
          <div className="flex justify-between">
            <Button
              ghost
              type="text"
              onClick={handleDrawer}
              icon={<DoubleRightOutlined />}
            />
            <Link href={`/user/${user?.id}`} target="_blank">
              <Button
                type="primary"
                shape="round"
                onClick={handleDrawer}
                icon={<LinkOutlined />}
              >
                {tcommon('user_profile_open_button')}
              </Button>
            </Link>
          </div>
        }
        placement="right"
        closable={false}
        onClose={handleDrawer}
        open={drawer}
        width={screens.md ? '500' : undefined}
      >
        <ProtectedCard type="MEMBER">
          {loading ? (
            <div className="text-center">
              <Spin />
            </div>
          ) : userData?.findUser ? (
            <UserInfo user={userData?.findUser} />
          ) : (
            <div className="space-y-4 text-center">
              <Avatar size={70} src="/data/avatar-1.jpeg" />
              <Skeleton />
              <Skeleton />
            </div>
          )}
        </ProtectedCard>
      </Drawer>
    </div>
  );
}

export default UserAvatar;
